import { useEffect, useId, useRef, useState, type FormEvent } from 'react';
import { Flag, X } from 'lucide-react';
import { api } from '../api.js';
import { useAccount } from '../Account.js';
import {
  reportReasons,
  type ReportReason,
  type ReportTarget,
} from '../../../../packages/shared/src/reports.js';

/**
 * A quiet "Report" control that opens a reason picker in a modal dialog. Works for guests;
 * signed-in members send their CSRF token so the report is linked to their account.
 */
export function ReportButton({
  target,
  id,
  subject,
  className = '',
}: {
  target: ReportTarget;
  id: string;
  subject: string;
  className?: string;
}) {
  const [open, setOpen] = useState(false),
    [done, setDone] = useState(false);
  return (
    <>
      <button
        type="button"
        className={('report-button ' + className).trim()}
        onClick={() => {
          if (!done) setOpen(true);
        }}
        // Stays focusable after sending, so focus can return here when the dialog closes.
        aria-disabled={done || undefined}
        aria-label={done ? 'Reported' : 'Report ' + subject}
      >
        <Flag size={13} aria-hidden="true" />
        {done ? 'Reported' : target === 'PROFILE' ? 'Report profile' : 'Report'}
      </button>
      {open && (
        <ReportDialog
          target={target}
          id={id}
          subject={subject}
          onClose={() => setOpen(false)}
          onSent={() => setDone(true)}
        />
      )}
    </>
  );
}

function ReportDialog({
  target,
  id: targetId,
  subject,
  onClose,
  onSent,
}: {
  target: ReportTarget;
  id: string;
  subject: string;
  onClose: () => void;
  onSent: () => void;
}) {
  const id = useId();
  const { user } = useAccount();
  const dialog = useRef<HTMLDialogElement>(null),
    heading = useRef<HTMLHeadingElement>(null),
    details = useRef<HTMLTextAreaElement>(null);
  const [reason, setReason] = useState<ReportReason | ''>(''),
    [text, setText] = useState(''),
    [website, setWebsite] = useState(''),
    [busy, setBusy] = useState(false),
    [error, setError] = useState(''),
    [sent, setSent] = useState(false);
  const reasons = reportReasons.filter((r) => (r.targets as readonly string[]).includes(target));
  const needsDetails = reason === 'OTHER';
  useEffect(() => {
    dialog.current?.showModal();
    heading.current?.focus();
  }, []);
  useEffect(() => {
    if (sent) heading.current?.focus();
  }, [sent]);
  function close() {
    if (!busy) dialog.current?.close();
  }
  async function submit(event: FormEvent) {
    event.preventDefault();
    setError('');
    if (!reason) {
      setError('Choose the reason that fits best.');
      return;
    }
    if (needsDetails && text.trim().length < 10) {
      setError('Tell us what’s wrong in a few words.');
      details.current?.focus();
      return;
    }
    setBusy(true);
    try {
      const csrf = user
        ? (await api<{ csrfToken: string }>('/api/v1/account/session')).csrfToken
        : '';
      await api('/api/v1/reports', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...(csrf ? { 'X-CSRF-Token': csrf } : {}) },
        body: JSON.stringify({ target, id: targetId, reason, details: text, website }),
      });
      setSent(true);
      onSent();
    } catch (failure) {
      setError((failure as Error).message);
    } finally {
      setBusy(false);
    }
  }
  return (
    <dialog
      ref={dialog}
      className="modal-dialog report-dialog"
      aria-labelledby={id + '-title'}
      aria-describedby={id + '-intro'}
      onClose={onClose}
      onCancel={(event) => {
        if (busy) event.preventDefault();
      }}
      onClick={(event) => {
        if (event.target === dialog.current) close();
      }}
    >
      <div className="modal-dialog-panel">
        <header className="modal-dialog-header">
          <h2 id={id + '-title'} ref={heading} tabIndex={-1}>
            {sent
              ? 'Thanks for letting us know'
              : target === 'PROFILE'
                ? 'Report this profile'
                : 'Report this review'}
          </h2>
          <button
            type="button"
            className="modal-close"
            onClick={close}
            disabled={busy}
            aria-label="Close"
          >
            <X size={18} aria-hidden />
          </button>
        </header>
        {sent ? (
          <div className="modal-dialog-sent">
            <p id={id + '-intro'}>
              An editor will look at this report and act if it breaks our standards. We don’t tell
              members who reported them.
            </p>
            <div className="modal-dialog-actions">
              <button type="button" className="button" onClick={close}>
                Done
              </button>
            </div>
          </div>
        ) : (
          <form onSubmit={submit} noValidate>
            <p id={id + '-intro'} className="modal-dialog-intro">
              You’re reporting {subject}. Editors review every report; we don’t tell the member who
              sent it.
            </p>
            <fieldset className="report-reasons" disabled={busy}>
              <legend>Why are you reporting it?</legend>
              {reasons.map((option) => (
                <label key={option.value} className={reason === option.value ? 'selected' : ''}>
                  <input
                    type="radio"
                    name={id + '-reason'}
                    value={option.value}
                    checked={reason === option.value}
                    onChange={() => {
                      setReason(option.value);
                      setError('');
                    }}
                  />
                  <span>
                    <strong>{option.label}</strong>
                    <small>{option.detail}</small>
                  </span>
                </label>
              ))}
            </fieldset>
            <label className="report-details" htmlFor={id + '-details'}>
              {needsDetails ? 'What’s wrong?' : 'Anything else we should know?'}
              {!needsDetails && <span> Optional</span>}
            </label>
            <textarea
              ref={details}
              id={id + '-details'}
              value={text}
              maxLength={1000}
              rows={3}
              disabled={busy}
              aria-describedby={id + '-count'}
              aria-invalid={needsDetails && error ? true : undefined}
              onChange={(event) => setText(event.target.value)}
              placeholder={needsDetails ? 'Describe the problem in a few words.' : ''}
            />
            <p id={id + '-count'} className="report-count">
              {text.length.toLocaleString('en-GB')} / 1,000
            </p>
            <label className="report-honeypot" aria-hidden="true">
              Website
              <input
                tabIndex={-1}
                autoComplete="off"
                value={website}
                onChange={(event) => setWebsite(event.target.value)}
              />
            </label>
            {error && (
              <p className="error-message" role="alert">
                {error}
              </p>
            )}
            <div className="modal-dialog-actions">
              <button type="button" className="modal-cancel" onClick={close} disabled={busy}>
                Cancel
              </button>
              <button className="button" disabled={busy}>
                <Flag size={15} aria-hidden="true" />
                {busy ? 'Sending…' : 'Send report'}
              </button>
            </div>
          </form>
        )}
      </div>
    </dialog>
  );
}
